import { BackendApiClient } from '../../auth/api/backend-client';
import { BackendValidationError } from '../../auth/api/investor';
import { requireList } from '../../../shared/lib/response-shape';
import type { ScopeContext } from '../../auth/model/types';

const backendClient = new BackendApiClient();

export interface Instrument {
  id: string;
  symbol: string;
  name: string;
  currency: string;
  status: string;
  instrument_type: string;
  exchange?: string | null;
  isin?: string | null;
  figi?: string | null;
}

export interface InstrumentResponse {
  instruments: Instrument[];
  cursor?: string;
}

export interface InstrumentCandidate {
  figi: string;
  isin: string;
  symbol: string;
  name: string;
  instrument_type: string;
  instrument_subtype: string;
  currency: string;
  country: string;
  exchange: string;
}

export interface InstrumentRequest {
  id: string;
  holder_id: string;
  status: string;
  isin: string;
  figi: string;
  tags: string[];
  created: string;
}

export interface InstrumentRequestEvent {
  id: string;
  type: string;
  reasons: string[];
  instrument_id: string;
  instrument_type: string;
  resource: InstrumentRequest;
}

const INSTRUMENT_PATH_BY_TYPE: Record<string, string> = {
  debt: '/debt-instrument',
  equity: '/equity-instrument',
};

const SERVER_PAGE_SIZE = 100;
const MAX_LISTED_INSTRUMENTS = 5000;
const MAX_INSTRUMENT_PAGES = MAX_LISTED_INSTRUMENTS / SERVER_PAGE_SIZE;

export interface InstrumentListing {
  instruments: Instrument[];
  isTruncated: boolean;
  isIncomplete: boolean;
}

interface InstrumentTypeOptions {
  status?: string;
  cursor?: string;
  limit?: number;
  ids?: string;
}

async function getInstrumentsOfType(
  sessionId: string,
  scopeContext: ScopeContext,
  instrumentType: string,
  options: InstrumentTypeOptions = {},
): Promise<InstrumentResponse> {
  const query = new URLSearchParams();
  if (options.status) {
    query.set('status', options.status);
  }
  if (options.ids) {
    query.set('ids', options.ids);
  }
  if (options.cursor) {
    query.set('cursor', options.cursor);
  }
  if (options.limit) {
    query.set('limit', String(options.limit));
  }
  const path = query.toString()
    ? `${INSTRUMENT_PATH_BY_TYPE[instrumentType]}?${query}`
    : INSTRUMENT_PATH_BY_TYPE[instrumentType];
  const response = await backendClient.get(path, sessionId, scopeContext);
  if (response.success && response.data) {
    const data = response.data;
    const instruments = requireList<Instrument>(data, 'instruments', 'Unreadable instrument response')
      .map((instrument) => ({ ...instrument, instrument_type: instrumentType }));
    return { instruments, cursor: data.cursor || undefined };
  }
  if (response.validationError) {
    throw response.validationError;
  }
  throw new BackendValidationError(response.error || 'Failed to fetch instruments');
}

async function getAllInstrumentsOfType(
  sessionId: string,
  scopeContext: ScopeContext,
  instrumentType: string,
  isCancelled?: () => boolean,
): Promise<{ instrumentById: Map<string, Instrument>; isTruncated: boolean; isIncomplete: boolean }> {
  const instrumentById = new Map<string, Instrument>();
  let cursor: string | undefined;
  for (let page = 0; page < MAX_INSTRUMENT_PAGES; page++) {
    if (isCancelled?.()) {
      return { instrumentById, isTruncated: false, isIncomplete: true };
    }
    let response: InstrumentResponse;
    try {
      response = await getInstrumentsOfType(sessionId, scopeContext, instrumentType, {
        status: 'active', cursor, limit: SERVER_PAGE_SIZE,
      });
    } catch (error) {
      if (instrumentById.size === 0) {
        throw error;
      }
      return { instrumentById, isTruncated: false, isIncomplete: true };
    }
    for (const instrument of response.instruments) {
      instrumentById.set(instrument.id, instrument);
    }
    cursor = response.cursor || undefined;
    if (!cursor) {
      return { instrumentById, isTruncated: false, isIncomplete: false };
    }
  }
  return { instrumentById, isTruncated: true, isIncomplete: false };
}

export async function getInstruments(
  sessionId: string,
  scopeContext: ScopeContext,
  filters?: { isCancelled?: () => boolean },
): Promise<InstrumentListing> {
  const { isCancelled } = filters ?? {};
  const legs = Object.keys(INSTRUMENT_PATH_BY_TYPE).map((instrumentType) =>
    getAllInstrumentsOfType(sessionId, scopeContext, instrumentType, isCancelled)
      .catch(() => ({
        instrumentById: new Map<string, Instrument>(), isTruncated: false, isIncomplete: true,
      })),
  );
  const results = await Promise.all(legs);
  const instrumentById = new Map<string, Instrument>();
  let isTruncated = false;
  let isIncomplete = false;
  for (const result of results) {
    for (const [id, instrument] of result.instrumentById) {
      instrumentById.set(id, instrument);
    }
    isTruncated = isTruncated || result.isTruncated;
    isIncomplete = isIncomplete || result.isIncomplete;
  }
  return { instruments: [...instrumentById.values()], isTruncated, isIncomplete };
}

export async function getInstrumentById(
  sessionId: string,
  scopeContext: ScopeContext,
  instrumentId: string,
  instrumentType: string,
): Promise<Instrument | null> {
  if (!INSTRUMENT_PATH_BY_TYPE[instrumentType]) {
    return null;
  }
  try {
    const response = await getInstrumentsOfType(
      sessionId, scopeContext, instrumentType, { ids: instrumentId },
    );
    return response.instruments[0] || null;
  } catch {
    return null;
  }
}

export async function previewInstruments(
  sessionId: string,
  accountScope: ScopeContext,
  symbol: string,
  country?: string,
): Promise<InstrumentCandidate[]> {
  const body = country ? { symbol, country } : { symbol };
  const response = await backendClient.post(
    '/instrument-request/preview', sessionId, body, accountScope,
  );
  if (response.success && response.data) {
    return (response.data as { previews: InstrumentCandidate[] }).previews || [];
  }
  if (response.validationError) {
    throw response.validationError;
  }
  throw new BackendValidationError(response.error || 'Failed to search instruments');
}

export async function createInstrumentRequest(
  sessionId: string,
  accountScope: ScopeContext,
  params: { isin?: string; figi?: string },
): Promise<InstrumentRequest> {
  const response = await backendClient.post(
    '/instrument-request', sessionId, { requests: [params] }, accountScope,
  );
  if (response.success && response.data) {
    const requests = (response.data as { requests: InstrumentRequest[] }).requests || [];
    return requests[0];
  }
  if (response.validationError) {
    throw response.validationError;
  }
  throw new BackendValidationError(response.error || 'Failed to request instrument');
}

export async function getInstrumentRequest(
  sessionId: string,
  accountScope: ScopeContext,
  requestId: string,
): Promise<InstrumentRequest | null> {
  const response = await backendClient.get(
    `/instrument-request?ids=${requestId}`, sessionId, accountScope,
  );
  if (response.success && response.data) {
    return (response.data as { requests: InstrumentRequest[] }).requests?.[0] || null;
  }
  return null;
}

async function getInstrumentRequestEvent(
  sessionId: string,
  accountScope: ScopeContext,
  requestId: string,
  eventType: string,
): Promise<InstrumentRequestEvent | null> {
  const response = await backendClient.get(
    `/instrument-request/event?types=${eventType}&limit=100`, sessionId, accountScope,
  );
  if (!response.success || !response.data) {
    return null;
  }
  const events = (response.data as { events: InstrumentRequestEvent[] }).events || [];
  return events.find((event) => event.resource?.id === requestId) || null;
}

export async function getResolvedInstrumentReference(
  sessionId: string,
  accountScope: ScopeContext,
  requestId: string,
): Promise<{ instrument_id: string; instrument_type: string } | null> {
  const matched = await getInstrumentRequestEvent(sessionId, accountScope, requestId, 'success');
  if (!matched?.instrument_id) {
    return null;
  }
  return { instrument_id: matched.instrument_id, instrument_type: matched.instrument_type };
}

export async function getInstrumentRequestFailureReasons(
  sessionId: string,
  accountScope: ScopeContext,
  requestId: string,
): Promise<string[]> {
  const matched = await getInstrumentRequestEvent(sessionId, accountScope, requestId, 'failed');
  return matched?.reasons || [];
}
